import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SkeletonMap } from "@/components/common/Skeletons";
import { ParkingMap } from "@/components/parking/ParkingMap";
import { neighbourRect, ringToRect, rectToRing } from "@/lib/parkingGeometry";
import { parkingService } from "@/services/api";
import { toast } from "sonner";
import { Loader2, Undo2, Eraser, Trash2, RotateCcw, TriangleAlert, Copy } from "lucide-react";

// Scan one area of a lot: four corners, grid settings, a generated preview
// that can be hand-corrected, then save.
//
//   mode "layout"  creates a new lot (name + its first area)
//   mode "area"    adds another area to an existing lot, with the lot's
//                  current slots shown for context and an overlap warning
//   mode "edit"    re-scans an existing area: its corners and settings are
//                  loaded, and saving keeps the slots that are still in the
//                  grid (same ids, same occupants) and only reshapes them
//
// The preview is generated by the backend (POST /api/parking/preview), not
// recomputed here, so what is previewed is by construction what is saved —
// there is only one implementation of the grid maths. Hand edits are made on a
// local copy of that preview and sent along on save.

const DEFAULTS = { cols: "6", rowHeightM: "5.5", rowsPerBlock: "2", roadGapM: "6.9" };
const PREVIEW_DEBOUNCE_MS = 350;
// The dialog animates in over ~200ms; a Google Map initialised mid-transform
// renders black, so the map mounts only once the dialog has settled (same
// reason AddFacilityDialog does it).
const MAP_MOUNT_DELAY_MS = 260;
const NEW = "new-";

function toNumber(v) {
  const n = Number(v);
  return v !== "" && Number.isFinite(n) ? n : null;
}

const sameRing = (a, b) =>
  a.length === b.length && a.every((p, i) => Math.abs(p[0] - b[i][0]) < 1e-9 && Math.abs(p[1] - b[i][1]) < 1e-9);

export function ParkingAreaDialog({ open, onOpenChange, facility, mode = "layout", layout, area: editArea, onSaved }) {
  const isLayout = mode === "layout";
  const isEdit = mode === "edit";
  const [lotName, setLotName] = useState("");
  const [areaName, setAreaName] = useState("");
  const [form, setForm] = useState(DEFAULTS);
  const [alerts, setAlerts] = useState({ enter: true, exit: true });
  const [corners, setCorners] = useState([]);
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState([]); // the preview's slots, as hand-edited
  const [editing, setEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!open) {
      setLotName("");
      setAreaName("");
      setForm(DEFAULTS);
      setAlerts({ enter: true, exit: true });
      setCorners([]);
      setPreview(null);
      setDraft([]);
      setEditing(false);
      setSelectedId(null);
      setPreviewError(null);
      setSaving(false);
      setMapMounted(false);
      return;
    }
    const timer = setTimeout(() => setMapMounted(true), MAP_MOUNT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // Editing an existing area starts from what it was scanned with.
  useEffect(() => {
    if (!open || !isEdit || !editArea) return;
    setAreaName(editArea.name);
    setCorners(editArea.corners.map((p) => [p[0], p[1]]));
    setForm({
      cols: String(editArea.cols), rowHeightM: String(editArea.rowHeightM),
      rowsPerBlock: String(editArea.rowsPerBlock), roadGapM: String(editArea.roadGapM),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editArea?.id]);

  const params = useMemo(() => ({
    cols: toNumber(form.cols),
    rowHeightM: toNumber(form.rowHeightM),
    rowsPerBlock: toNumber(form.rowsPerBlock),
    roadGapM: toNumber(form.roadGapM),
  }), [form.cols, form.rowHeightM, form.rowsPerBlock, form.roadGapM]);

  const paramsValid = Number.isInteger(params.cols) && params.cols >= 1
    && params.rowHeightM != null && params.rowHeightM >= 1
    && Number.isInteger(params.rowsPerBlock) && params.rowsPerBlock >= 1 && params.rowsPerBlock <= 10
    && params.roadGapM != null && params.roadGapM >= 0;

  // Regenerate the preview whenever the corners or grid settings change. Only
  // the newest request may write state: typing "12" fires a request for "1"
  // then "12", and the slower answer must not overwrite the faster one.
  useEffect(() => {
    if (corners.length !== 4 || !paramsValid) {
      requestSeq.current += 1;
      setPreview(null);
      setDraft([]);
      setPreviewError(null);
      setPreviewing(false);
      return;
    }
    const seq = ++requestSeq.current;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const result = await parkingService.preview({
          corners, ...params, layoutId: layout?.id, excludeAreaId: isEdit ? editArea?.id : undefined,
        });
        if (seq !== requestSeq.current) return;
        setPreview(result);
        setDraft(result.slots.map((s) => ({ ...s, id: `${NEW}${s.row}-${s.col}` })));
        setSelectedId(null);
        setPreviewError(null);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setPreview(null);
        setDraft([]);
        setPreviewError(err.message);
      } finally {
        if (seq === requestSeq.current) setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [corners, params, paramsValid, layout?.id, isEdit, editArea?.id]);

  // What changed relative to the generated grid.
  const original = useMemo(() => {
    const map = new Map();
    (preview?.slots || []).forEach((s) => map.set(`${NEW}${s.row}-${s.col}`, s.polygon));
    return map;
  }, [preview]);
  const movedCount = draft.filter((s) => original.has(s.id) && !sameRing(original.get(s.id), s.polygon)).length;
  const addedCount = draft.filter((s) => !original.has(s.id)).length;
  const removedCount = preview ? preview.slots.filter((s) => !draft.some((d) => d.id === `${NEW}${s.row}-${s.col}`)).length : 0;
  const editCount = movedCount + removedCount + addedCount;

  // Provisional slot numbers, in the order the server will assign them. A new
  // area continues after the lot's highest number; when re-scanning an area, a
  // slot that is still in the grid keeps the number it has.
  const lotMax = useMemo(() => (layout?.slots || []).reduce((m, s) => Math.max(m, s.number), 0), [layout]);
  const keptNumbers = useMemo(() => {
    const byCell = new Map();
    if (isEdit && editArea) {
      (layout?.slots || []).filter((s) => s.areaId === editArea.id).forEach((s) => byCell.set(`${s.row}-${s.col}`, s.number));
    }
    return byCell;
  }, [isEdit, editArea, layout]);
  const numberedDraft = useMemo(() => {
    const ordered = [...draft].sort((a, b) => a.row - b.row || a.col - b.col);
    let next = lotMax;
    const numbers = new Map();
    ordered.forEach((s) => {
      const kept = keptNumbers.get(`${s.row}-${s.col}`);
      numbers.set(s.id, kept ?? ++next);
    });
    return draft.map((s) => ({ ...s, number: numbers.get(s.id), preview: true }));
  }, [draft, lotMax, keptNumbers]);

  const contextSlots = useMemo(
    () => (isLayout ? [] : (layout?.slots || []).filter((s) => !isEdit || s.areaId !== editArea?.id)),
    [isLayout, isEdit, layout, editArea]
  );
  const mapSlots = useMemo(() => [...contextSlots, ...numberedDraft], [contextSlots, numberedDraft]);
  // The lot's other areas, each tagged with its name, plus the area being drawn
  // (tagged with the name it will get) once it has four corners.
  const draftName = areaName.trim()
    || (isEdit ? editArea?.name : isLayout ? "Area 1" : `Area ${(layout?.areas?.length ?? 0) + 1}`);
  const outlines = useMemo(() => {
    const others = isLayout ? [] : (layout?.areas || []).filter((a) => !isEdit || a.id !== editArea?.id)
      .map((a) => ({ id: a.id, name: a.name, corners: a.corners }));
    return corners.length === 4 ? [...others, { id: "draft", name: draftName, corners }] : others;
  }, [isLayout, isEdit, layout, editArea, corners, draftName]);

  const addCorner = ({ lat, lng }) => setCorners((c) => (c.length >= 4 ? c : [...c, [lat, lng]]));
  const moveCorner = (i, { lat, lng }) => setCorners((c) => c.map((p, idx) => (idx === i ? [lat, lng] : p)));
  const onSlotChange = (id, ring) => setDraft((d) => d.map((s) => (s.id === id ? { ...s, polygon: ring } : s)));
  const dupCounter = useRef(0);
  const duplicateSelected = () => {
    const src = draft.find((s) => s.id === selectedId);
    if (!src) return;
    const id = `${NEW}dup-${++dupCounter.current}`;
    setDraft((d) => [...d, { ...src, id, polygon: rectToRing(neighbourRect(ringToRect(src.polygon))) }]);
    setSelectedId(id);
  };
  const onSlotClick = (id) => {
    if (editing && String(id).startsWith(NEW)) setSelectedId((cur) => (cur === id ? null : id));
  };
  const deleteSelected = () => {
    setDraft((d) => d.filter((s) => s.id !== selectedId));
    setSelectedId(null);
  };
  const resetEdits = () => {
    if (!preview) return;
    setDraft(preview.slots.map((s) => ({ ...s, id: `${NEW}${s.row}-${s.col}` })));
    setSelectedId(null);
  };

  const canSave = !!preview && draft.length > 0 && !saving && !previewing
    && (!isLayout || lotName.trim().length > 0);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const area = {
      name: areaName.trim() || undefined,
      corners,
      ...params,
      // Only sent when the grid was actually touched; otherwise the server
      // regenerates the identical slots itself.
      slots: editCount > 0 ? draft : undefined,
    };
    try {
      if (isLayout) {
        const created = await parkingService.create({
          facilityId: facility.id, name: lotName.trim(), area,
          alertOnEnter: alerts.enter, alertOnExit: alerts.exit,
        });
        toast.success(`Parking lot saved — ${created.totalSlots} slots`);
        onSaved?.(created);
      } else if (isEdit) {
        const { overlaps } = await parkingService.updateArea(editArea.id, area);
        toast.success("Area updated");
        if (overlaps.length) toast.warning(`This area overlaps ${overlaps.join(", ")}`);
        onSaved?.();
      } else {
        const { overlaps } = await parkingService.addArea(layout.id, area);
        toast.success("Area added");
        if (overlaps.length) toast.warning(`This area overlaps ${overlaps.join(", ")}`);
        onSaved?.();
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const center = facility?.latitude != null && facility?.longitude != null
    ? { lat: facility.latitude, lng: facility.longitude }
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl" data-testid="parking-area-dialog">
        <DialogHeader>
          <DialogTitle>
            {isLayout ? "Add parking lot" : isEdit ? `Edit ${editArea?.name ?? "area"}` : `Add area to ${layout?.name ?? "lot"}`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Move the corners or change the grid. Slots that are still in the grid keep their number and whoever is parked in them; saving replaces any hand edits made to this area's slots."
              : "Click the four corners of one patch of the lot, set the grid, and fix any slot that is off before saving. Add as many areas as you need — each can have its own settings."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-[1fr_300px]">
          <div className="h-[500px]">
            {mapMounted ? (
              <ParkingMap
                center={center}
                zoom={19}
                corners={corners}
                outlines={outlines}
                slots={mapSlots}
                interaction={editing ? "slots" : "view"}
                selectedSlotId={selectedId}
                editableCorners
                onMapClick={addCorner}
                onCornerMove={moveCorner}
                onSlotClick={onSlotClick}
                onSlotChange={onSlotChange}
                fitKey={preview ? `p-${preview.slotCount}-${preview.rows}-${corners.length}` : `c-${corners.length}`}
                testId="parking-draw-map"
              />
            ) : (
              <SkeletonMap className="h-full" />
            )}
          </div>

          <div className="max-h-[500px] space-y-3 overflow-y-auto pr-1">
            {isLayout && (
              <div className="space-y-1.5">
                <Label htmlFor="parking-lot-name">Lot name</Label>
                <Input id="parking-lot-name" value={lotName} onChange={(e) => setLotName(e.target.value)} placeholder="e.g. Gate 3 lot" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="parking-area-name">Area name <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="parking-area-name" value={areaName} onChange={(e) => setAreaName(e.target.value)} placeholder={isLayout ? "Area 1" : isEdit ? editArea?.name : `Area ${(layout?.areas?.length ?? 0) + 1}`} />
            </div>

            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/20 px-3 py-2 text-sm">
              <span>
                Corners <span className="font-telemetry text-muted-foreground">{corners.length}/4</span>
              </span>
              <span className="flex gap-1">
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={corners.length === 0}
                  onClick={() => setCorners((c) => c.slice(0, -1))} aria-label="Undo last corner">
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={corners.length === 0}
                  onClick={() => setCorners([])} aria-label="Clear corners">
                  <Eraser className="h-4 w-4" />
                </Button>
              </span>
            </div>
            {corners.length === 4 && (
              <p className="text-xs text-muted-foreground">Drag a numbered corner to adjust it; the slots follow.</p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="parking-cols">Slots per row</Label>
                <Input id="parking-cols" inputMode="numeric" value={form.cols} onChange={setField("cols")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="parking-row">Stall depth (m)</Label>
                <Input id="parking-row" inputMode="decimal" value={form.rowHeightM} onChange={setField("rowHeightM")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="parking-block">Rows per block</Label>
                <Input id="parking-block" inputMode="numeric" value={form.rowsPerBlock} onChange={setField("rowsPerBlock")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="parking-gap">Aisle (m)</Label>
                <Input id="parking-gap" inputMode="decimal" value={form.roadGapM} onChange={setField("roadGapM")} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              A block is rows of stalls that sit back to back (2 for a normal lot, nose to nose).
              The aisle is the driving lane after every block, not after every row.
            </p>

            <div className="min-h-[3.25rem] rounded-xl border border-border/60 bg-secondary/20 px-3 py-2 text-sm">
              {previewing ? (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                </span>
              ) : previewError ? (
                <span className="text-red-500">{previewError}</span>
              ) : preview ? (
                <div>
                  <div className="font-medium">
                    {draft.length} slots <span className="text-muted-foreground">({preview.rows} rows × {preview.cols})</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Area {preview.widthM} × {preview.heightM} m · each slot about {preview.slotWidthM} m wide
                  </div>
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {corners.length < 4 ? `Place ${4 - corners.length} more corner${corners.length === 3 ? "" : "s"} on the map.` : "Enter valid grid settings."}
                </span>
              )}
            </div>

            {preview?.overlaps?.length > 0 && (
              <div className="flex gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  This area overlaps {preview.overlaps.join(", ")}. A vehicle on the shared ground could be matched
                  to either. You can still save, but you probably want to move a corner.
                </span>
              </div>
            )}

            {preview && (
              <div className="space-y-2 rounded-xl border border-border/60 bg-secondary/20 px-3 py-2 text-sm">
                <label className="flex items-center justify-between gap-3">
                  <span>
                    Edit slots
                    <span className="block text-xs text-muted-foreground">Click a slot, then drag it to move, its handles to resize, or the centre knob to rotate.</span>
                  </span>
                  <Switch checked={editing} onCheckedChange={(v) => { setEditing(v); setSelectedId(null); }} />
                </label>
                {editing && (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={!selectedId} onClick={duplicateSelected}>
                      <Copy className="mr-1.5 h-3.5 w-3.5" /> Duplicate
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled={!selectedId} onClick={deleteSelected}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete slot
                    </Button>
                    <Button type="button" size="sm" variant="ghost" disabled={editCount === 0} onClick={resetEdits}>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset edits
                    </Button>
                  </div>
                )}
                {editCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {[
                      movedCount > 0 && `${movedCount} changed`,
                      addedCount > 0 && `${addedCount} added`,
                      removedCount > 0 && `${removedCount} removed`,
                    ].filter(Boolean).join(", ")}. Moving a corner or changing the grid
                    regenerates the slots and discards these edits, so set those first.
                  </p>
                )}
              </div>
            )}

            {isLayout && (
              <div className="space-y-2 text-sm">
                <label className="flex items-center justify-between gap-3">
                  <span>Alert when a vehicle parks</span>
                  <Switch checked={alerts.enter} onCheckedChange={(v) => setAlerts((a) => ({ ...a, enter: v }))} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Alert when a vehicle leaves</span>
                  <Switch checked={alerts.exit} onCheckedChange={(v) => setAlerts((a) => ({ ...a, exit: v }))} />
                </label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={!canSave} data-testid="parking-area-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLayout ? "Save lot" : isEdit ? "Save changes" : "Save area"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
